import type { UseAsyncStateOptions, UseAsyncStateReturn } from '@vueuse/core';
import { useAsyncState } from '@vueuse/core';
import dayjs from 'dayjs';
import { SHA256 } from 'crypto-js';
import _ from 'lodash';
import { $t } from '@/locales';
import type { Api } from '@/typings/api';
import type { TableColumn } from '@/components/advanced/basic-table/use-table';
import type { CommonType } from '@/typings/common';

/**
 * Transform record to option
 *
 * @example
 *   ```ts
 *   const record = {
 *     key1: 'label1',
 *     key2: 'label2'
 *   };
 *   const options = transformRecordToOption(record);
 *   // [
 *   //   { value: 'key1', label: 'label1' },
 *   //   { value: 'key2', label: 'label2' }
 *   // ]
 *   ```;
 *
 * @param record
 */
export function transformRecordToOption<
  T extends Record<string, string | CommonType.TagOption<string | number>>,
  K = 'string' | 'number'
>(record: T, valueDataType?: K) {
  return Object.entries(record).map(([value, label]) => ({
    value: valueDataType === 'number' ? Number(value) : value,
    label: typeof label === 'string' ? label : label.label,
    config: typeof label === 'string' ? undefined : label.config
  })) as CommonType.Option<keyof T>[];
}

/**
 * Translate options
 *
 * @param options
 */
export function translateOptions(options: CommonType.Option<string>[]) {
  return options.map(option => ({
    ...option,
    label: $t(option.label as App.I18n.I18nKey)
  }));
}

/**
 * Toggle html class
 *
 * @param className
 */
export function toggleHtmlClass(className: string) {
  function add() {
    document.documentElement.classList.add(className);
  }

  function remove() {
    document.documentElement.classList.remove(className);
  }

  return {
    add,
    remove
  };
}
/**
 * 判断过滤条件是否存在
 *
 * @param filters
 * @param column
 * @returns
 */
export function hasFilter(filters: Api.Common.Filter[], column: string) {
  return filters.some(item => item.column === column);
}
/**
 * 获取过滤条件
 *
 * @param filters
 * @param column
 * @returns
 */
export function getFilter(filters: Api.Common.Filter[], column: string) {
  return filters.find(item => item.column === column);
}

/**
 * 添加过滤条件
 *
 * @param filters
 * @param filter
 */
export function addFilter(filters: Api.Common.Filter[], filter: Api.Common.Filter) {
  const index = filters.findIndex(item => item.column === filter.column);
  if (index === -1) {
    filters.push(filter);
  } else {
    filters[index] = filter;
  }
}

/**
 * 添加多个过滤条件
 *
 * @param filters
 * @param newFilters
 */
export function addFilters(filters: Api.Common.Filter[], newFilters: Api.Common.Filter[]) {
  if (newFilters && newFilters.length > 0) {
    newFilters.forEach(newFilter => {
      addFilter(filters, newFilter);
    });
  }
}

/**
 * 删除过滤条件
 *
 * @param filters
 * @param column
 */
export function removeFilter(filters: Api.Common.Filter[], column: string) {
  const index = filters.findIndex(item => item.column === column);
  if (index !== -1) {
    filters.splice(index, 1);
  }
}
/**
 * 从值对象中获取值
 *
 * @param value 待格式化值
 * @param record 值对象
 * @param defaultValue 默认值
 * @returns 格式化后值
 */
export function formatValue<K = string | number>(
  value: string | number | null | undefined,
  record: Record<string | number, CommonType.TagOption<K> | string>,
  defaultValue: string | number | null = null
) {
  if (value === null || value === undefined) {
    return defaultValue;
  }
  const result = record[value];
  if (result === null || result === undefined) {
    return defaultValue;
  }
  if (typeof result === 'string') {
    return result;
  }
  return result.label;
}

/**
 * 金额格式化工具
 *
 * @param value
 * @param record
 * @param defaultValue
 */
export const moneyFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 3
});
/**
 * 百分比格式化工具
 *
 * @param value
 * @returns
 */
export const percentFormatter = new Intl.NumberFormat(undefined, {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 3
});
/**
 * 根据数据类型格式化
 *
 * @param value
 * @param dataType
 * @returns
 */
export function formatValueByDataType(value: any, dataType: CommonType.DataType) {
  if (value === null || value === undefined) {
    return '';
  } else if (Array.isArray(value)) {
    return value.join('、');
  } else if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  switch (dataType) {
    case 'boolean':
      return value ? '是' : '否';
    case 'money':
      return moneyFormatter.format(value);
    case 'percent':
      return percentFormatter.format(value);
    case 'date':
      return dayjs(value).format('YYYY-MM-DD');
    case 'datetime':
      return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
    case 'time':
      return dayjs(value).format('HH:mm:ss');
    default:
      return value;
  }
}

// export function formatTableCellValue(
//   value: any,
//   dataType: CommonType.DataType,
//   enumRecord: Record<string | number, CommonType.TagOption<string | number> | string>
// ) {
//   if (value === null || value === undefined) {
//     return '';
//   } else if (Array.isArray(value)) {
//     return value.join('、');
//   } else if (typeof value === 'object') {
//     return JSON.stringify(value);
//   }
// }

/**
 * 构建树形数据
 *
 * @param data
 * @param config
 */
export function buildTreeData<T extends Record<string, any>>(
  data: T[],
  config: { parentKey: string; childrenKey: string; keyField: string; clearEmptyChildren?: boolean } = {
    parentKey: 'parent',
    childrenKey: 'children',
    keyField: 'id',
    clearEmptyChildren: false
  }
) {
  const treeRoot: T[] = [];
  const nodeMap = new Map<string, T>();
  for (const item of data) {
    nodeMap.set(item[config.keyField], item);
  }
  for (const item of data) {
    const parentId = _.get(item, config.parentKey);
    if (parentId && nodeMap.has(parentId)) {
      const parentNode = nodeMap.get(parentId);
      if (parentNode) {
        if (parentNode[config.childrenKey] === undefined) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          parentNode[config.childrenKey] = [];
        }
        parentNode[config.childrenKey]?.push(nodeMap.get(item[config.keyField]));
      }
    } else {
      const node: any = nodeMap.get(item[config.keyField]);
      if (node[config.childrenKey] && node[config.childrenKey].length === 0) {
        node[config.childrenKey] = undefined;
      } else if (config.clearEmptyChildren) {
        clearEmptyChildren(node, config.childrenKey);
      }
      treeRoot.push(node as T);
    }
  }
  return treeRoot;
}
/**
 * 清除空子节点
 *
 * @param obj
 * @param childrenKey
 */
function clearEmptyChildren(obj: any, childrenKey: string) {
  if (!obj[childrenKey] || obj[childrenKey].length === 0) {
    obj[childrenKey] = undefined;
  } else {
    for (const child of obj[childrenKey]) {
      clearEmptyChildren(child, childrenKey);
    }
  }
}

/**
 * 根据校验函数移除对象属性
 *
 * @param obj 待处理对象
 * @param predicate 校验函数
 */
export function omitObject(obj: Record<string, any> | any[], predicate: (value: any, key: string) => boolean): void {
  if (Array.isArray(obj)) {
    for (const iterator of obj) {
      if (iterator && typeof iterator === 'object') {
        omitObject(iterator, predicate);
      }
    }
  } else {
    const keys = Object.keys(obj);
    for (const key of keys) {
      const value = obj[key];
      if (predicate(value, key)) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete obj[key];
      } else if (value && typeof value === 'object') {
        omitObject(value, predicate);
      }
    }
  }
}

/**
 * 根据 object对象的path路径获取值
 *
 * 如果路径中有数组对象，则返回最终值数组
 *
 * @param obj
 * @param keyPath
 */
export function getValueByKeyPath(obj: Record<string, any>, keyPath: string, defaultValue = null): any | any[] | null {
  if (obj && keyPath) {
    const keyPathArr = keyPath.split(/\.|\[|\]/);
    let value = obj;
    for (const key of keyPathArr) {
      if (value) {
        if (Array.isArray(value)) {
          value = value.map(item => item[key]);
        } else if (typeof value === 'object') {
          value = value[key];
        }
      } else {
        return defaultValue;
      }
    }
    return value;
  }
  return defaultValue;
}

const cacheLastData: Record<string, any> = {};
/**
 * 缓存方法执行结果，在缓存时间结束后清除缓存
 *
 * @param fn 待执行方法
 * @param cacheKey 缓存Key
 * @param cacheTime 缓存时间
 * @param args 方法参数
 * @returns 方法执行结果
 */
// eslint-disable-next-line max-params
export function cacheFunction<F extends (...args: any) => any>(
  fn: F,
  cacheKey: string | number,
  cacheTime: number = 500,
  ...args: Parameters<F>
): ReturnType<F> {
  if (cacheKey) {
    if (cacheLastData[cacheKey] !== undefined) {
      return cacheLastData[cacheKey];
    }
    cacheLastData[cacheKey] = fn(...args);
    setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete cacheLastData[cacheKey];
    }, cacheTime);
    return cacheLastData[cacheKey];
  }
  return fn(...args);
}

/**
 * 将驼峰命名法的字符串转换为连字符分隔的小写格式
 *
 * @param {string} str - 驼峰命名法的字符串
 * @returns {string} - 连字符分隔的小写格式的字符串
 */
export function camelToKebabCase(str: string): string {
  return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}
/**
 * 抽取表格列配置
 *
 * @param columns 列配置
 * @param keys 指定key
 * @returns
 */
export function pickTableColumn<T>(columns: TableColumn<T>[], keys: string[]): TableColumn<T>[] {
  return columns.filter(column => keys.includes((column as any).key));
}
/**
 * 获取表格列配置
 *
 * @param columns 列配置
 * @param keys 排除key
 * @returns
 */
export function omitTableColumn<T>(columns: TableColumn<T>[], keys: string[]): TableColumn<T>[] {
  return columns.filter(column => !keys.includes((column as any).key));
}

const cacheMap = new Map<string, { promise: Promise<any>; expireTime: number }>();
/**
 * 缓存Promise结果
 *
 * @param key 缓存key
 * @param promise Promise
 * @param expireTime 过期时间(ms)
 * @returns
 */
export function cachePromise(key: string, promise: Promise<any>, expireTime = 5000) {
  if (cacheMap.has(key)) {
    return cacheMap.get(key)?.promise;
  }
  cacheMap.set(key, { promise, expireTime: Date.now() + expireTime });
  return promise;
}

export function getCachePromise(key: string) {
  if (cacheMap.has(key) && cacheMap.get(key)!.expireTime > Date.now()) {
    return cacheMap.get(key)?.promise;
  }
  cacheMap.delete(key);
  return null;
}

const cacheAsyncState = new Map<
  string,
  {
    keys: string[];
    state: UseAsyncStateReturn<any, any, any>;
  }
>();
/**
 * 异步状态 获取异步状态后缓存结果，供同一请求多次请求使用
 *
 * @param key 缓存key
 * @param componentId 组件ID，用于区分不同组件，当组件销毁时清除缓存
 * @param promise Promise
 * @param initialState 初始状态
 * @param options 选项
 */
// eslint-disable-next-line max-params
export function getAsyncState<Data, Params extends any[] = [], Shallow extends boolean = true>(
  key: string,
  componentId: string,
  promise: Promise<Data> | ((...args: Params) => Promise<Data>),
  initialState: Data,
  options?: UseAsyncStateOptions<Shallow, Data>
): UseAsyncStateReturn<Data, Params, Shallow> {
  const keyEncoder = SHA256(key).toString();
  // 转换为Base64缩短长度
  const keyStr = btoa(keyEncoder);
  if (cacheAsyncState.has(keyStr)) {
    cacheAsyncState.get(keyStr)?.keys.push(componentId);
    return cacheAsyncState.get(keyStr)?.state as UseAsyncStateReturn<Data, Params, Shallow>;
  }
  const asyncState = useAsyncState(promise, initialState, options);
  cacheAsyncState.set(keyStr, { state: asyncState, keys: [componentId] });
  return asyncState;
}
/**
 * 根据key获取异步状态
 *
 * @param key 缓存key
 * @returns UseAsyncStateReturn<Data, Params, Shallow> | null
 */
export function getAsyncStateByKey<Data, Params extends any[] = [], Shallow extends boolean = true>(
  key: string
): UseAsyncStateReturn<Data, Params, Shallow> | null {
  const keyEncoder = SHA256(key).toString();

  const keyStr = btoa(keyEncoder);
  if (cacheAsyncState.has(keyStr)) {
    return cacheAsyncState.get(keyStr)?.state as UseAsyncStateReturn<Data, Params, Shallow>;
  }
  return null;
}

/**
 * 根据componentId获取异步状态
 *
 * @param componentId
 */
export function getAsyncStateByComponentId<Data, Params extends any[] = [], Shallow extends boolean = true>(
  componentId: string
): UseAsyncStateReturn<Data, Params, Shallow> | null {
  for (const [, cache] of cacheAsyncState) {
    if (cache.keys.includes(componentId)) {
      return cache.state as UseAsyncStateReturn<Data, Params, Shallow>;
    }
  }
  return null;
}

/**
 * 清除异步状态缓存
 *
 * @param key 缓存key
 * @param componentId 组件ID
 */
export function clearAsyncState(key: string, componentId: string): void {
  const keyEncoder = SHA256(key).toString();
  // 转换为Base64缩短长度
  const keyStr = btoa(keyEncoder);
  if (cacheAsyncState.has(keyStr)) {
    const cache = cacheAsyncState.get(keyStr);
    cache?.keys.splice(cache.keys.indexOf(componentId), 1);
    if (cache?.keys.length === 0) {
      cacheAsyncState.delete(keyStr);
    }
  }
}
/**
 * 清除异步状态缓存
 *
 * @param componentId 组件ID
 */
export function clearAsyncStateByComponentId(componentId: string): void {
  for (const [key, cache] of cacheAsyncState) {
    if (cache.keys.includes(componentId)) {
      cache.keys.splice(cache.keys.indexOf(componentId), 1);
      if (cache.keys.length === 0) {
        cacheAsyncState.delete(key);
      }
      break;
    }
  }
}
